import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MangaCard from "@/components/common/MangaCard";
import JsonLd from "@/components/common/JsonLd";
import useSeo from "@/hooks/useSeo";
import { getMangaById, mangas, formatViews } from "@/mocks/manga";

export default function ArticlePage() {
  const { id } = useParams();
  const manga = getMangaById(Number(id));
  const [liked, setLiked] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const related = manga
    ? mangas
        .filter((m) => m.id !== manga.id && m.categories.some((c) => manga.categories.includes(c)))
        .slice(0, 8)
    : [];

  useSeo({
    title: manga ? manga.title : "漫画不存在",
    description: manga
      ? `${manga.title}：${manga.description}，${manga.status}中${manga.categories.join("、")}分类漫画，已更新${manga.chapters}话，点击免费在线阅读。`
      : "未找到该漫画作品。",
    keywords: `91漫画,${manga ? manga.title : ""},漫画分类,91漫画人气榜`,
    path: `/article/${id}`,
  });

  if (!manga) {
    return (
      <section className="pt-32 pb-20 text-center px-4">
        <i className="ri-ghost-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-xl font-semibold text-foreground-900">漫画不存在或已下架</h1>
        <Link to="/" className="inline-block mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600">
          返回首页
        </Link>
      </section>
    );
  }

  const chapterList = Array.from({ length: Math.min(manga.chapters, 60) }, (_, i) => manga.chapters - i);
  const visibleChapters = showAll ? chapterList : chapterList.slice(0, 24);

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://zhuanpan91.github.io/" },
            { "@type": "ListItem", position: 2, name: `${manga.categories[0]}漫画`, item: `https://zhuanpan91.github.io/category/${manga.categories[0]}` },
            { "@type": "ListItem", position: 3, name: manga.title, item: `https://zhuanpan91.github.io/article/${manga.id}` },
          ],
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: manga.title,
          image: manga.cover,
          author: { "@type": "Organization", name: manga.author },
          publisher: { "@type": "Organization", name: "91漫画" },
          dateModified: manga.updateTime,
          description: manga.description,
          articleSection: manga.categories.join("、"),
          inLanguage: "zh-CN",
          url: `https://zhuanpan91.github.io/article/${manga.id}`,
        }}
      />

      {/* header */}
      <section className="pt-24 pb-10 bg-gradient-to-b from-primary-100/70 to-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <nav aria-label="面包屑导航" className="text-xs text-foreground-500 mb-6">
            <ol className="flex items-center gap-1 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li className="text-foreground-400">/</li>
              <li>
                <Link to={`/category/${manga.categories[0]}`} className="hover:text-primary-600">
                  {manga.categories[0]}漫画
                </Link>
              </li>
              <li className="text-foreground-400">/</li>
              <li aria-current="page" className="text-foreground-700">{manga.title}</li>
            </ol>
          </nav>

          <div className="flex flex-col sm:flex-row gap-6 md:gap-8">
            <img
              src={manga.cover}
              alt={`${manga.title}漫画封面`}
              title={manga.title}
              className="w-full sm:w-56 md:w-64 shrink-0 rounded-lg object-cover object-top border border-background-200/70"
            />
            <div className="min-w-0">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950">{manga.title}</h1>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${manga.status === "完结" ? "bg-secondary-500 text-background-50" : "bg-primary-500 text-background-50"}`}>
                  {manga.status}
                </span>
                {manga.categories.map((c) => (
                  <Link key={c} to={`/category/${c}`} className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs hover:bg-accent-200">
                    #{c}
                  </Link>
                ))}
                {manga.isHot && <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs">热门推荐</span>}
                {manga.isNew && <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs">新作</span>}
              </div>

              <dl className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <dt className="text-xs text-foreground-500">作者</dt>
                  <dd className="mt-1 font-medium text-foreground-800">{manga.author}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">章节</dt>
                  <dd className="mt-1 font-medium text-foreground-800">{manga.chapters} 话</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">人气</dt>
                  <dd className="mt-1 font-medium text-foreground-800">{formatViews(manga.views)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">评分</dt>
                  <dd className="mt-1 font-medium text-accent-600">
                    <i className="ri-star-fill mr-1"></i>
                    {manga.rating.toFixed(1)}
                  </dd>
                </div>
              </dl>

              <p className="mt-5 text-sm leading-relaxed text-foreground-700">{manga.description}</p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button type="button" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 cursor-pointer">
                  <i className="ri-play-fill mr-1"></i>
                  开始阅读第{manga.chapters}话
                </button>
                <button
                  type="button"
                  onClick={() => setLiked((v) => !v)}
                  className={`px-6 py-3 rounded-full border text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    liked
                      ? "bg-primary-100 border-primary-300 text-primary-600"
                      : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  <i className={`${liked ? "ri-heart-fill" : "ri-heart-line"} mr-1`}></i>
                  {liked ? "已加入书架" : "加入书架"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* chapters */}
      <section className="py-10 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="flex items-center gap-2 text-xl md:text-2xl text-foreground-950 mb-5">
            <i className="ri-list-ordered-2 text-primary-500 text-2xl"></i>
            章节列表
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {visibleChapters.map((ch) => (
              <a
                key={ch}
                href="#"
                className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="truncate">第 {ch} 话</span>
                <i className="ri-play-circle-line text-primary-500"></i>
              </a>
            ))}
          </div>
          {chapterList.length > 24 && (
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="px-6 py-2.5 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap cursor-pointer"
              >
                {showAll ? "收起章节" : "查看全部章节"}
                <i className={`ml-1 ri-arrow-down-s-line ${showAll ? "rotate-180" : ""}`}></i>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* related */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="flex items-center gap-2 text-xl md:text-2xl text-foreground-950 mb-5">
            <i className="ri-link text-primary-500 text-2xl"></i>
            相关推荐
          </h2>
          {related.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop="manga-list">
              {related.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-600">暂无相关推荐</p>
          )}
        </div>
      </section>
    </>
  );
}